import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AssignmentReturn as ReturnIcon,
} from '@mui/icons-material';
import {
  getPurchaseOrderReturns,
  createPurchaseOrderReturn,
  getReturnReasons,
} from '../../collections/purchaseOrders';

const brandColors = {
  primary: '#0e2a04',
  secondary: '#6b8e4e',
  accent: '#d4a574',
  light: '#c8d6b8',
  background: '#f5f8f3',
};

const POReturnsSection = ({ purchaseOrderId, purchaseOrder, controlRole, onReturnCreated }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [returnsData, setReturnsData] = useState(null);
  const [expandedReturns, setExpandedReturns] = useState(new Set());
  const [reasons, setReasons] = useState([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState('');

  // Form state
  const [selectedItems, setSelectedItems] = useState({});
  const [notes, setNotes] = useState('');
  const [adjustInventory, setAdjustInventory] = useState(true);

  // Status check - only allow returns for received POs
  const canCreateReturn = ['PARTIALLY_RECEIVED', 'FULLY_RECEIVED', 'CLOSED'].includes(
    purchaseOrder?.status?.toUpperCase()
  );

  useEffect(() => {
    fetchReturns();
    fetchReasons();
  }, [purchaseOrderId]);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const response = await getPurchaseOrderReturns(purchaseOrderId);
      setReturnsData(response.data);
    } catch (err) {
      setError(err.message || 'Lỗi khi tải dữ liệu trả hàng');
    } finally {
      setLoading(false);
    }
  };

  const fetchReasons = async () => {
    try {
      const response = await getReturnReasons();
      setReasons(response.data || []);
    } catch (err) {
      console.error('Error fetching reasons:', err);
    }
  };

  const handleOpenDialog = () => {
    // Calculate already returned quantities from returnsData
    const alreadyReturnedMap = {};
    returnsData?.returns?.forEach((ret) => {
      ret.items?.forEach((retItem) => {
        // Map by po_item_id (which corresponds to item.id in purchaseOrder.items)
        // Since retItem has product_id, we need to find the PO item
        const poItem = purchaseOrder?.items?.find((i) => i.product_id === retItem.product_id);
        if (poItem) {
          alreadyReturnedMap[poItem.id] = (alreadyReturnedMap[poItem.id] || 0) + retItem.quantity_returned;
        }
      });
    });

    // Initialize selected items with all items unchecked
    const initialSelection = {};
    purchaseOrder?.items?.forEach((item) => {
      const alreadyReturned = alreadyReturnedMap[item.id] || 0;
      initialSelection[item.id] = {
        selected: false,
        quantity_returned: '',
        reason: 'DEFECTIVE',
        reason_notes: '',
        max_returnable: item.quantity_ordered - alreadyReturned,
        already_returned: alreadyReturned,
      };
    });
    setSelectedItems(initialSelection);
    setNotes('');
    setAdjustInventory(true);
    setDialogError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItems({});
    setNotes('');
    setDialogError('');
  };

  const handleItemSelectionChange = (itemId, checked) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], selected: checked },
    }));
  };

  const handleItemFieldChange = (itemId, field, value) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value },
    }));
  };

  const getSelectedItemsForSubmit = () => {
    return Object.entries(selectedItems)
      .filter(([, data]) => data.selected && parseFloat(data.quantity_returned) > 0)
      .map(([itemId, data]) => ({
        po_item_id: parseInt(itemId),
        quantity_returned: parseFloat(data.quantity_returned),
        reason: data.reason,
        reason_notes: data.reason_notes || null,
      }));
  };

  const calculateTotalReturnAmount = () => {
    let total = 0;
    Object.entries(selectedItems).forEach(([itemId, data]) => {
      if (data.selected && data.quantity_returned) {
        const item = purchaseOrder?.items?.find((i) => i.id === parseInt(itemId));
        if (item) {
          total += parseFloat(data.quantity_returned) * (item.unit_cost || 0);
        }
      }
    });
    return total;
  };

  const handleSubmit = async () => {
    const items = getSelectedItemsForSubmit();
    if (items.length === 0) {
      setDialogError('Vui lòng chọn ít nhất một sản phẩm để trả');
      return;
    }

    // Validate quantities
    for (const item of items) {
      const selected = selectedItems[item.po_item_id];
      if (item.quantity_returned > selected.max_returnable) {
        const product = purchaseOrder?.items?.find((i) => i.id === item.po_item_id);
        setDialogError(`Số lượng trả của ${product?.product?.name} vượt quá số lượng có thể trả (${selected.max_returnable})`);
        return;
      }
    }

    try {
      setSubmitting(true);
      setDialogError('');
      const response = await createPurchaseOrderReturn(purchaseOrderId, {
        items,
        notes: notes || null,
        adjust_inventory: adjustInventory,
      });
      handleCloseDialog();
      fetchReturns();
      if (onReturnCreated) {
        onReturnCreated();
      }
      // Show success message with inventory info
      const inventoryMsg = response.data?.inventory_adjusted
        ? 'Đã điều chỉnh tồn kho'
        : 'Không điều chỉnh tồn kho';
      alert(`Tạo phiếu trả thành công!\n${inventoryMsg}`);
    } catch (err) {
      setDialogError(err.response?.data?.message || err.message || 'Lỗi khi tạo phiếu trả hàng');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleReturnExpansion = (returnId) => {
    setExpandedReturns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(returnId)) {
        newSet.delete(returnId);
      } else {
        newSet.add(returnId);
      }
      return newSet;
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 4, borderRadius: '16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Đang tải dữ liệu trả hàng...</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        sx={{
          mb: 4,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: `0 8px 32px rgba(14, 42, 4, 0.08)`,
        }}
      >
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${brandColors.background} 0%, #ffffff 100%)`,
            borderBottom: `2px solid ${brandColors.accent}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: brandColors.primary,
              fontWeight: 600,
              fontFamily: 'Poppins, Inter, system-ui, sans-serif',
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                width: '4px',
                height: '24px',
                backgroundColor: brandColors.accent,
                borderRadius: '2px',
                marginRight: '12px',
              },
            }}
          >
            <ReturnIcon sx={{ mr: 1 }} />
            Hàng trả lại
            {returnsData?.returns_count > 0 && (
              <Chip
                label={returnsData.returns_count}
                size="small"
                sx={{ ml: 1, backgroundColor: brandColors.accent, color: 'white' }}
              />
            )}
          </Typography>

          {controlRole !== 'STAFF' && canCreateReturn && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                background: `linear-gradient(135deg, ${brandColors.accent} 0%, #c4956a 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, #c4956a 0%, ${brandColors.accent} 100%)`,
                },
              }}
            >
              Tạo phiếu trả
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {returnsData?.returns?.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: '#fafafa',
                    '& .MuiTableCell-head': {
                      fontWeight: 600,
                      color: brandColors.primary,
                    },
                  }}
                >
                  <TableCell sx={{ width: 50 }} />
                  <TableCell>Mã phiếu</TableCell>
                  <TableCell>Ngày trả</TableCell>
                  <TableCell>Số SP</TableCell>
                  <TableCell align="right">Tổng tiền trả</TableCell>
                  <TableCell>Người tạo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {returnsData.returns.map((ret) => (
                  <React.Fragment key={ret.id}>
                    <TableRow
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'rgba(212, 165, 116, 0.05)' },
                      }}
                    >
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleReturnExpansion(ret.id)}>
                          {expandedReturns.has(ret.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ret.return_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(ret.return_date)}</TableCell>
                      <TableCell>
                        <Chip label={`${ret.items?.length || 0} SP`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: '#d32f2f' }}
                        >
                          -{formatCurrency(ret.total_return_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{ret.created_by?.name || '-'}</TableCell>
                    </TableRow>

                    {/* Expanded Items */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedReturns.has(ret.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 4, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                              Chi tiết sản phẩm trả
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Sản phẩm</TableCell>
                                  <TableCell align="center">SL trả</TableCell>
                                  <TableCell align="right">Đơn giá</TableCell>
                                  <TableCell align="right">Thành tiền</TableCell>
                                  <TableCell>Lý do</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {ret.items?.map((item) => (
                                  <TableRow key={item.id}>
                                    <TableCell>{item.product_name}</TableCell>
                                    <TableCell align="center">{item.quantity_returned}</TableCell>
                                    <TableCell align="right">{formatCurrency(item.unit_cost)}</TableCell>
                                    <TableCell align="right" sx={{ color: '#d32f2f' }}>
                                      -{formatCurrency(item.return_amount)}
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title={item.reason_notes || ''}>
                                        <Chip
                                          label={item.reason_label}
                                          size="small"
                                          sx={{
                                            backgroundColor: '#fff3e0',
                                            color: '#e65100',
                                          }}
                                        />
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {ret.notes && (
                              <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff', borderRadius: '8px' }}>
                                <Typography variant="body2" color="textSecondary">
                                  <strong>Ghi chú:</strong> {ret.notes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}

                {/* Total row */}
                <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                  <TableCell colSpan={4}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Tổng tiền đã trả
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                      -{formatCurrency(returnsData.total_returned_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <ReturnIcon sx={{ fontSize: 48, color: brandColors.light, mb: 2 }} />
            <Typography variant="body1" color="textSecondary">
              Chưa có phiếu trả hàng nào
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Create Return Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: brandColors.primary }}>
          Tạo phiếu trả hàng
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            Chọn sản phẩm trả:
          </Typography>

          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Sản phẩm</TableCell>
                  <TableCell align="center">Đã nhận</TableCell>
                  <TableCell align="center">Có thể trả</TableCell>
                  <TableCell align="center" sx={{ width: 100 }}>
                    SL trả
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>Lý do</TableCell>
                  <TableCell sx={{ width: 150 }}>Ghi chú</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrder?.items?.map((item) => {
                  const selection = selectedItems[item.id] || {};
                  return (
                    <TableRow key={item.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selection.selected || false}
                          onChange={(e) => handleItemSelectionChange(item.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>{item.product?.name}</TableCell>
                      <TableCell align="center">{item.quantity_ordered}</TableCell>
                      <TableCell align="center">{selection.max_returnable || 0}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={selection.quantity_returned || ''}
                          onChange={(e) =>
                            handleItemFieldChange(item.id, 'quantity_returned', e.target.value)
                          }
                          disabled={!selection.selected}
                          inputProps={{ min: 0, max: selection.max_returnable, step: 0.1 }}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth disabled={!selection.selected}>
                          <Select
                            value={selection.reason || 'DEFECTIVE'}
                            onChange={(e) => handleItemFieldChange(item.id, 'reason', e.target.value)}
                          >
                            {reasons.map((r) => (
                              <MenuItem key={r.value} value={r.value}>
                                {r.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={selection.reason_notes || ''}
                          onChange={(e) =>
                            handleItemFieldChange(item.id, 'reason_notes', e.target.value)
                          }
                          disabled={!selection.selected}
                          placeholder="Chi tiết..."
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TextField
            fullWidth
            label="Ghi chú chung"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 3 }}
          />

          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: adjustInventory ? '#e3f2fd' : '#fff3e0',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Checkbox
              checked={adjustInventory}
              onChange={(e) => setAdjustInventory(e.target.checked)}
              sx={{ mr: 1 }}
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Điều chỉnh tồn kho
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {adjustInventory
                  ? 'Tạo giao dịch trừ tồn kho ngay khi trả hàng'
                  : 'Không tạo giao dịch tồn kho (stocktake sẽ xử lý)'}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: '#fff3e0',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Tổng tiền trả:
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f' }}>
              -{formatCurrency(calculateTotalReturnAmount())}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Hủy
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <ReturnIcon />}
            sx={{
              background: `linear-gradient(135deg, ${brandColors.accent} 0%, #c4956a 100%)`,
            }}
          >
            {submitting ? 'Đang xử lý...' : 'Xác nhận trả hàng'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default POReturnsSection;
